/**
 * Inicio y Configuración — los pasos de activación de `OD-0015 D-02`, con su
 * estado REAL. Desde el 2026-09-18 (propietario) los pasos se ven en
 * Configuración, y el Inicio es el resumen del negocio.
 *
 * `OD-0015 D-04` prohíbe datos de ejemplo, y `D-06` pide que un estado vacío
 * diga qué es, por qué está vacío y qué hacer. Por eso aquí no hay tarjetas de
 * "facturado este mes" con ceros ni gráficas inventadas: sólo lo que se sabe.
 *
 * Qué se sabe hoy y de dónde sale:
 *
 *   cuenta, email      hay sesión: sólo se entra con el email ya confirmado
 *                      (el usuario nace al verificarlo, URS §7)
 *   organización       /api/v1/me responde con una organización
 *   datos fiscales     GET /api/v1/legal-entity: HECHO sólo si están CONFIRMADOS
 *                      (TDS-CORE R-E4); guardados sin confirmar = pendiente
 *   primer cliente     GET /api/v1/usage/customer-slots: hecho con una plaza usada
 *   serie, factura     esos módulos no existen todavía: NO pueden estar hechos.
 *                      Se marcan "aún no disponible", no "pendiente", para no
 *                      pedir una acción que la aplicación no permite.
 *
 * ## Resumen del negocio (bloque 1, 2026-09-18)
 *
 * Lo que YA existe: presupuestos por estado, lo enviado y lo aceptado este mes
 * y los últimos presupuestos. Todo sale de la API; las sumas las hace el
 * servidor. "Enviado" y "aceptado" NO se llaman ingresos: un presupuesto
 * aceptado no es una factura ni un cobro.
 *
 * ## Orden de la pantalla (2026-10-01)
 *
 * Antes lo primero que se veía eran los CONTADORES DEL PLAN: cuatro barras de
 * progreso diciendo cuánto queda para chocar con un límite. Eso es
 * administración, no trabajo. Ahora el orden es:
 *
 *   1. tareas pendientes   lo que hay que hacer hoy, cada una con su botón
 *   2. resumen del negocio lo que ha pasado
 *   3. uso del plan        al final, y sólo destacado cuando de verdad aprieta
 *
 * Los límites NO se esconden (`URS §110-113` prohíbe descubrirlos al chocar):
 * bajan de sitio. Y quien está cerca de uno lo ve resaltado, con el nivel que
 * da la API (`warningLevel`, `URS §54`), no con un umbral inventado aquí.
 *
 * ## Business: las oportunidades en vez de los límites (2026-10-18)
 *
 * Decisión del propietario: en Business la tarjeta de límites no tiene sentido
 * y en su sitio van las oportunidades abiertas del CRM, con sus importes, para
 * seguirlas de un vistazo. No contradice §110-113: Business no tiene ningún
 * límite numérico de unidades (`BUSINESS_V1` en `packages/plans`, todo `null`,
 * `RC-0009`), así que la tarjeta sólo decía «N · sin límite» cinco veces. No
 * había ningún límite que descubrir al chocar.
 *
 * Se decide con la versión del plan que da la API. Mientras no se sabe, los
 * límites se siguen viendo: esconderlos sin saber el plan sí podría esconder un
 * límite de verdad.
 *
 * ## Business: los impuestos del trimestre (2026-10-20)
 *
 * Petición del propietario: «ver en inicio el IVA y demás como un indicador».
 * Es la MISMA respuesta que la pantalla de Informes fiscales (`OD-0041`), en
 * pequeño: IVA repercutido, retenciones y el próximo plazo. Del trimestre en
 * curso, SALVO mientras está abierto el plazo del anterior (del 1 al 20 del mes
 * siguiente): entonces el que importa es el que se está declarando. Ni una
 * suma más: las cifras las da la API. Y con su aviso de
 * estimación parcial, porque una cifra de IVA sin él se lee como «lo que hay
 * que pagar» (`D-02`), que es justo lo que no es.
 *
 * Desde el 2026-10-25 también el IVA soportado de las compras anotadas
 * (`OD-0056`, enmienda de `D-07`), siempre con su aviso corto al lado.
 */
import { useEffect, useState } from "react";
import {
  api,
  type CuotasPresupuestos,
  type EstadoPresupuesto,
  type InformeFiscal,
  type IvaSoportadoDelInforme,
  type ListaOportunidades,
  type Plazas,
  type ResumenPresupuestos,
  type UsoFacturas,
} from "../api/cliente";
import { t } from "../i18n/es";
import { Anillo } from "../ui/Anillo";
import { importeLegible } from "../ui/formato";
import { Icono } from "../ui/Iconos";
import { InsigniaEstado } from "../ui/Insignia";
import { Aviso, Boton, FilaPulsable, Tarjeta } from "../ui/primitivas";
import { pasosDelAlta, progresoDelAlta, type DatosDelAlta, type IdPasoAlta, type PasoAlta } from "../ui/pasos-del-alta";
import { avisosDeLaTarjeta } from "./Avisos";
import type { Aviso as AvisoPendiente, DestinoAviso } from "./Avisos";
import { Obligacion } from "./PantallaInformesFiscales";
import { diaLegible } from "./PantallaOportunidades";

const ESTADOS: readonly EstadoPresupuesto[] = ["DRAFT", "SENT", "ACCEPTED", "REJECTED", "EXPIRED", "CANCELLED"];

function Indicador({
  titulo,
  usados,
  limite,
  detalle,
  aprieta = false,
}: {
  titulo: string;
  usados: number;
  limite: number | null;
  detalle?: string;
  /** Lo dice la API (warningLevel / plazas sin hueco), no un umbral de aquí. */
  aprieta?: boolean;
}) {
  return (
    <div className={aprieta ? "indicador indicador-aprieta" : "indicador"}>
      <p className="indicador-titulo">{titulo}</p>
      <p className="indicador-valor">
        {limite === null ? t.inicio.resumen.sinLimite(usados) : t.inicio.resumen.deLimite(usados, limite)}
      </p>
      {limite === null ? null : <progress value={usados} max={limite} aria-label={titulo} />}
      {detalle !== undefined ? <p className="ayuda-neutra">{detalle}</p> : null}
    </div>
  );
}

/**
 * Tareas pendientes: lo mismo que cuenta la campana de avisos, pero en el sitio
 * donde se entra a trabajar, y cada una con el botón que lleva a resolverla.
 *
 * No calcula nada nuevo: reutiliza `calcularAvisos`, que ya sólo habla de lo que
 * la API sabe. Si no hay nada, lo dice ("Todo al día") en vez de desaparecer:
 * una pantalla sin tareas y una pantalla que no ha cargado no pueden verse igual.
 */
/*
  LA LISTA UNICA DE PENDIENTES.

  Hasta el 2026-10-15 esta tarjeta mezclaba DOS modelos de tarea: una fila que
  contaba «quedan N pasos» y, debajo, los avisos. Y se solapaban a medias:

    fiscal    salia CONTADO en la fila y AVISADO debajo
    cliente   solo contado
    factura   solo contado
    mfa       solo avisado
    AEAT      solo avisado

  Por eso el numero nunca cuadraba con las filas: la captura del propietario
  decia «Quedan 3 pasos» encima de 3 avisos que eran otra cosa.

  Ahora hay UNA lista y cada cosa aparece UNA vez: los avisos del alta, mas los
  pasos que ningun aviso cubre, mas lo de operacion al final.
*/
function TareasPendientes({
  tareas,
  onIr,
  onIrAConfiguracion,
}: {
  tareas: readonly AvisoPendiente[] | null;
  onIr: (destino: DestinoAviso) => void;
  onIrAConfiguracion: () => void;
}) {
  if (tareas === null) return null;
  const hayAlta = tareas.some((a) => a.clase === "alta");
  return (
    <Tarjeta titulo={t.inicio.tareasTitulo}>
      {tareas.length === 0 ? <p className="ayuda-neutra">{t.inicio.tareasVacio}</p> : null}
      {tareas.length > 0 ? (
        <ul className="lista-pendientes lista-tareas">
          {tareas.map((a) => (
            <li key={a.id} className={a.tono === "aviso" ? "pendiente-urgente" : undefined}>
              <span>{a.texto}</span>
              <Boton variante="secundario" onClick={() => onIr(a.destino)}>
                {t.avisos.ir[a.destino]}
              </Boton>
            </li>
          ))}
        </ul>
      ) : null}
      {/*
        La lista completa de los siete pasos -con lo ya hecho y lo que hace
        Faktelio solo- sigue en Configuracion, y AL PIE, no como una fila que
        compita con las tareas. Antes era la primera fila y con un contador que
        no cuadraba con lo de debajo.
      */}
      {hayAlta ? (
        <div className="acciones">
          <Boton variante="secundario" onClick={onIrAConfiguracion}>
            {t.inicio.verPasos}
          </Boton>
        </div>
      ) : null}
    </Tarjeta>
  );
}

/** Cuántas oportunidades se ven en Inicio. El resto, en Oportunidades. */
const OPORTUNIDADES_EN_INICIO = 5;

/**
 * Las oportunidades abiertas, para seguirlas sin salir de Inicio. Van primero
 * las que antes se espera cerrar: ese orden lo da la API.
 *
 * El total lo suma la API (`openTotal`), igual que en la pantalla del CRM: esta
 * web no hace aritmética de dinero (`URS §129`). Y como allí, son importes
 * ESTIMADOS y sin IVA, no ingresos.
 *
 * Si la API no contesta con la lista (sin el CRM en el plan, sin permiso en el
 * rol, sin red) la tarjeta no aparece: no es un error de Inicio.
 */
function OportunidadesAbiertas({
  onAbrir,
  onNueva,
  onVerTodas,
}: {
  onAbrir: (id: string) => void;
  onNueva: () => void;
  onVerTodas: () => void;
}) {
  const [lista, setLista] = useState<ListaOportunidades | null>(null);

  useEffect(() => {
    void (async () => {
      const r = await api.listarOportunidades("OPEN");
      if (r.ok) setLista(r.datos);
    })();
  }, []);

  if (lista === null) return null;
  const visibles = lista.oportunidades.slice(0, OPORTUNIDADES_EN_INICIO);

  return (
    <Tarjeta titulo={t.inicio.oportunidades.titulo}>
      {lista.abiertas === 0 ? (
        <p className="ayuda-neutra">{t.inicio.oportunidades.vacio}</p>
      ) : (
        <>
          <dl className="cifras">
            <div>
              <dt>{t.inicio.oportunidades.abiertas(lista.abiertas)}</dt>
              <dd>{importeLegible(lista.totalAbierto)} €</dd>
            </div>
          </dl>
          <p className="ayuda-neutra">{t.inicio.oportunidades.estimados}</p>
          {/*
            Filas y no tabla: cinco columnas no caben en el movil, y la tabla
            se cortaba justo por el importe. Es la misma fila que la lista de
            Oportunidades.
          */}
          <ul className="lista-filas">
            {visibles.map((o) => (
              <FilaPulsable key={o.id} onClick={() => onAbrir(o.id)}>
                <span className="fila-titulo">
                  {o.title} · {o.estimatedAmount === null ? t.oportunidades.sinImporte : t.oportunidades.importe(importeLegible(o.estimatedAmount))}
                </span>
                <span className="ayuda-neutra">
                  {o.customer === null ? t.oportunidades.clienteEliminado : o.customer.displayName}
                  {" · "}
                  {/* Con presupuesto enlazado, la etapa la dice el presupuesto (OD-0048-ENMIENDA-01). */}
                  {t.oportunidades.etapas[o.stage]}
                  {" · "}
                  {o.expectedCloseDate === null ? t.oportunidades.sinFechaPrevista : t.oportunidades.previstaPara(diaLegible(o.expectedCloseDate))}
                </span>
              </FilaPulsable>
            ))}
          </ul>
        </>
      )}
      <div className="acciones">
        <Boton onClick={onNueva}>
          <Icono nombre="mas" />
          {t.oportunidades.nueva}
        </Boton>
        {lista.abiertas > 0 ? (
          <Boton variante="secundario" onClick={onVerTodas}>
            {lista.abiertas > visibles.length ? t.inicio.oportunidades.verTodas(lista.abiertas) : t.inicio.resumen.verTodos}
          </Boton>
        ) : null}
      </div>
    </Tarjeta>
  );
}

const ORDEN_MODELOS: readonly string[] = ["303", "130", "390"];

/**
 * El IVA de las compras anotadas (`OD-0056`, enmienda de `D-07` del
 * 2026-10-25). Con su aviso corto SIEMPRE al lado: sin el se lee como lo que se
 * puede deducir, y no lo es. Sin cifra, la frase de su caso (`D-04`), nunca un
 * 0,00. Y si el rol no ve las compras, nada: la tarjeta no explica lo que no
 * ensena, para eso esta Informes fiscales.
 */
function SoportadoDelTrimestre({ s }: { s: IvaSoportadoDelInforme }) {
  if (!s.disponible) {
    const frase = t.inicio.impuestos.soportadoSinCifra[s.motivo];
    return frase === undefined ? null : <p className="ayuda-neutra">{frase}</p>;
  }
  return (
    <>
      <dl className="cifras">
        <div>
          <dt>{t.inicio.impuestos.soportado}</dt>
          <dd>{t.informes.soportadoCifra(importeLegible(s.soportado))}</dd>
        </div>
      </dl>
      <p className="ayuda-neutra">{t.inicio.impuestos.soportadoAviso}</p>
    </>
  );
}

/**
 * Los impuestos del trimestre, de un vistazo (`OD-0041`, Business).
 *
 * Como las oportunidades: si la API no da el informe (otro plan, un rol sin
 * `invoice.read`, sin red) la tarjeta no aparece. No es un error de Inicio, y
 * la pantalla de Informes fiscales ya explica cada caso.
 */
function ImpuestosDelTrimestre({ onVerInforme }: { onVerInforme: () => void }) {
  const [datos, setDatos] = useState<InformeFiscal | null>(null);

  useEffect(() => {
    void (async () => {
      const r = await api.informeFiscal(null);
      if (!r.ok) return;
      // Con el plazo de un trimestre anterior ABIERTO, se ensena ese: es el que
      // se esta declarando. Si esa segunda lectura falla, el en curso.
      const declarandose = r.datos.calendario.proximas.find(
        (o) => o.publicado && o.estado === "ABIERTO" && o.periodo.includes("-T") && o.periodo !== r.datos.trimestre.etiqueta,
      );
      if (declarandose === undefined) {
        setDatos(r.datos);
        return;
      }
      const anterior = await api.informeFiscal(declarandose.periodo);
      setDatos(anterior.ok ? anterior.datos : r.datos);
    })();
  }, []);

  if (datos === null) return null;
  const inf = datos.informe;
  // Los plazos que acaban PRIMERO, todos (el 303 y el 130 suelen ir juntos), y
  // el 303 delante. Si ninguno tiene fecha publicada, el primero de la lista.
  const conFecha = datos.calendario.proximas.flatMap((o) => (o.publicado ? [o] : []));
  const primero = conFecha[0];
  const plazos =
    primero === undefined
      ? datos.calendario.proximas.slice(0, 1)
      : conFecha.filter((o) => o.hasta === primero.hasta).sort((a, b) => ORDEN_MODELOS.indexOf(a.modelo) - ORDEN_MODELOS.indexOf(b.modelo));

  return (
    <Tarjeta titulo={t.inicio.impuestos.titulo(t.informes.periodo(datos.trimestre.etiqueta))}>
      <p className="ayuda-neutra">
        {datos.trimestre.enCurso ? null : <strong>{t.inicio.impuestos.seDeclara} </strong>}
        {datos.ivaSoportado.disponible ? t.inicio.impuestos.parcialConCompras : t.inicio.impuestos.parcial}
      </p>
      {!inf.calculado ? (
        <p className="ayuda-neutra">{t.inicio.impuestos.sinDatos}</p>
      ) : inf.facturas.length === 0 ? (
        <p className="ayuda-neutra">{t.inicio.impuestos.sinFacturas}</p>
      ) : (
        <dl className="cifras">
          <div>
            <dt>{t.informes.ivaTitulo}</dt>
            <dd>{t.informes.ivaCifra(importeLegible(inf.iva.repercutido))}</dd>
          </div>
          <div>
            <dt>{t.inicio.impuestos.retenciones}</dt>
            <dd>{t.informes.retencionesCifra(importeLegible(inf.retenciones.importe))}</dd>
          </div>
        </dl>
      )}
      {inf.calculado && inf.facturas.length > 0 ? <p className="ayuda-neutra">{t.inicio.impuestos.ivaAyuda}</p> : null}
      <SoportadoDelTrimestre s={datos.ivaSoportado} />
      {plazos.length > 0 ? (
        <>
          <h3 className="subtitulo">{plazos.length === 1 ? t.inicio.impuestos.proximoPlazo : t.inicio.impuestos.proximosPlazos}</h3>
          <ul className="informe-plazos">
            {plazos.map((o) => (
              <Obligacion key={`${o.modelo}|${o.periodo}`} o={o} hoy={datos.aFecha} />
            ))}
          </ul>
        </>
      ) : null}
      <div className="acciones">
        <Boton variante="secundario" onClick={onVerInforme}>
          {t.inicio.impuestos.verInforme}
        </Boton>
      </div>
    </Tarjeta>
  );
}

function ResumenNegocio({
  mostrarLimites,
  usoFacturas,
  onIrAPresupuestos,
  onNuevoPresupuesto,
  onAbrirPresupuesto,
}: {
  /** `false` sólo cuando se sabe que el plan no tiene límites (Business). */
  mostrarLimites: boolean;
  usoFacturas: UsoFacturas | null;
  onIrAPresupuestos: () => void;
  onNuevoPresupuesto: () => void;
  onAbrirPresupuesto: (id: string) => void;
}) {
  const [resumen, setResumen] = useState<ResumenPresupuestos | null>(null);
  const [cuotas, setCuotas] = useState<CuotasPresupuestos | null>(null);
  const [plazas, setPlazas] = useState<Plazas | null>(null);
  const [cargado, setCargado] = useState(false);

  /*
    Si hay algo que contar. Decide si los indicadores van ABIERTOS.

    Cuenta como "hay algo" tanto lo consumido como lo que APRIETA, y lo que
    aprieta lo dice LA API -`warningLevel`, `available === 0`-, nunca un umbral
    escrito aqui: duplicar el umbral en el cliente es como se separan.

    `null` -mientras no se sabe- NO cuenta como uso: no se abre una tarjeta
    afirmando que hay consumo antes de saberlo.
  */
  const hayUso =
    (cuotas !== null &&
      [cuotas.quotesCreatedThisMonth, cuotas.quotesActive, cuotas.products].some(
        (c) => c.used > 0 || c.warningLevel !== null,
      )) ||
    (plazas !== null && (plazas.used > 0 || plazas.available === 0)) ||
    (usoFacturas?.issued != null &&
      (usoFacturas.issued.month.used > 0 ||
        usoFacturas.issued.year.used > 0 ||
        usoFacturas.issued.month.warningLevel !== null));

  useEffect(() => {
    void (async () => {
      const [r, c, p] = await Promise.all([api.resumenPresupuestos(), api.cuotasPresupuestos(), api.plazasClientes()]);
      if (r.ok) setResumen(r.datos);
      if (c.ok) setCuotas(c.datos);
      if (p.ok) setPlazas(p.datos);
      setCargado(true);
    })();
  }, []);

  // Sin permiso o sin red: el resumen simplemente no aparece; los pasos, sí.
  if (!cargado || resumen === null) return null;

  const total = ESTADOS.reduce((n, e) => n + (resumen.byStatus[e] ?? 0), 0);

  return (
    <>
      {total === 0 ? (
        <Aviso tono="info" titulo={t.inicio.resumen.vacioTitulo}>
          {t.inicio.resumen.vacioDetalle}
        </Aviso>
      ) : (
        <div className="rejilla-resumen">
          <Tarjeta titulo={t.inicio.resumen.esteMes}>
            <dl className="cifras">
              <div>
                <dt>{t.inicio.resumen.enviado(resumen.month.sentCount)}</dt>
                <dd>{importeLegible(resumen.month.sentTotal)} €</dd>
              </div>
              <div>
                <dt>{t.inicio.resumen.aceptado(resumen.month.acceptedCount)}</dt>
                <dd>{importeLegible(resumen.month.acceptedTotal)} €</dd>
              </div>
            </dl>
            <p className="ayuda-neutra">{t.inicio.resumen.noSonIngresos}</p>
          </Tarjeta>
          <Tarjeta titulo={t.inicio.resumen.porEstado}>
            <Anillo
              total={total}
              etiquetaTotal={t.inicio.resumen.porEstado}
              partes={ESTADOS.map((e) => ({ clave: e, etiqueta: t.presupuestos.estados[e], valor: resumen.byStatus[e] ?? 0 }))}
            />
          </Tarjeta>
        </div>
      )}

      <Tarjeta titulo={t.inicio.resumen.ultimos}>

        {resumen.recent.length > 0 ? (
          <table className="tabla">
            <thead>
              <tr>
                <th scope="col">{t.inicio.resumen.numero}</th>
                <th scope="col">{t.inicio.resumen.cliente}</th>
                <th scope="col">{t.inicio.resumen.estado}</th>
                <th scope="col">{t.inicio.resumen.total}</th>
              </tr>
            </thead>
            <tbody>
              {resumen.recent.map((q) => (
                <tr key={q.id}>
                  <td>
                    <button type="button" className="enlace" onClick={() => onAbrirPresupuesto(q.id)}>
                      {q.number}
                    </button>
                  </td>
                  <td>{q.customerName ?? ""}</td>
                  <td>
                    <InsigniaEstado estado={q.status} />
                  </td>
                  <td>{q.invoiceTotal === null ? "" : `${importeLegible(q.invoiceTotal)} €`}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : null}
        <div className="acciones">
          <Boton onClick={onNuevoPresupuesto}>
            <Icono nombre="mas" />
            {t.inicio.resumen.crear}
          </Boton>
          {resumen.recent.length > 0 ? (
            <Boton variante="secundario" onClick={onIrAPresupuestos}>
              {t.inicio.resumen.verTodos}
            </Boton>
          ) : null}
        </div>
      </Tarjeta>

      {/*
        Lo ultimo: los limites del plan.

        PLEGADOS MIENTRAS NO SE HAYA USADO NADA, y el comentario que habia aqui
        decia "se ven siempre (URS §110-113)", que es MAS ESTRICTO QUE EL
        REQUISITO. Lo que §110-113 obliga es a EXPLICAR limite, consumo,
        restante, reset, acciones y upgrade, y prohibe enseñar solo
        `LIMIT_REACHED`; y `OD-0015` D-06 añade que un estado vacio debe decir
        "que es esto, por que esta vacio, cual es la siguiente accion".

        Cinco contadores a cero no cumplen ninguna de esas tres cosas: son
        numeros sin explicacion, ocupando el primer dia el sitio donde deberia
        estar la unica accion que importa. Asi que cuando no hay nada que
        contar se dice EN PALABRAS -que es lo que pide D-06- y los contadores
        quedan a un clic, no escondidos: un `details` sigue estando en la
        pagina y lo encuentra tambien un lector de pantalla.

        En Business no se pinta: no tiene ningun limite que contar (ver la
        cabecera).
      */}
      {mostrarLimites ? (
        <Tarjeta titulo={t.inicio.usoTitulo}>
          {!hayUso ? <p className="ayuda-neutra">{t.inicio.usoSinNada}</p> : null}
          <details className="uso-plan" open={hayUso}>
            <summary>{t.inicio.usoVerDetalle}</summary>
            <p className="ayuda-neutra">{t.inicio.usoDetalle}</p>
            <div className="indicadores">
            {cuotas !== null ? (
              <>
                <Indicador
                  titulo={t.inicio.resumen.creadosMes}
                  usados={cuotas.quotesCreatedThisMonth.used}
                  limite={cuotas.quotesCreatedThisMonth.limit}
                  detalle={t.inicio.resumen.reinicio(cuotas.quotesCreatedThisMonth.resetsOn)}
                  aprieta={cuotas.quotesCreatedThisMonth.warningLevel !== null}
                />
                <Indicador
                  titulo={t.inicio.resumen.abiertos}
                  usados={cuotas.quotesActive.used}
                  limite={cuotas.quotesActive.limit}
                  aprieta={cuotas.quotesActive.warningLevel !== null}
                />
                <Indicador
                  titulo={t.inicio.resumen.productos}
                  usados={cuotas.products.used}
                  limite={cuotas.products.limit}
                  aprieta={cuotas.products.warningLevel !== null}
                />
              </>
            ) : null}
            {plazas !== null ? (
              <Indicador
                titulo={t.inicio.resumen.clientes}
                usados={plazas.used}
                limite={plazas.total}
                aprieta={plazas.available === 0}
              />
            ) : null}
            {/* El cupo de facturas emitidas (URS §68) no se veía en ninguna
                pantalla: se descubría al chocar con él al emitir. */}
            {usoFacturas?.issued != null ? (
              <Indicador
                titulo={t.inicio.resumen.facturasMes}
                usados={usoFacturas.issued.month.used}
                limite={usoFacturas.issued.month.limit}
                detalle={
                  usoFacturas.issued.year.limit === null
                    ? t.inicio.resumen.facturasAnioSinLimite(usoFacturas.issued.year.used)
                    : t.inicio.resumen.facturasAnio(usoFacturas.issued.year.used, usoFacturas.issued.year.limit)
                }
                aprieta={usoFacturas.issued.month.warningLevel !== null}
              />
            ) : null}
            </div>
          </details>
        </Tarjeta>
      ) : null}
    </>
  );
}

/** Desde la auditoría del 2026-10-29 vive con LA lista de pasos; se reexporta para no mover a quien lo importa. */
export type { EstadoEmisor } from "../ui/pasos-del-alta";

/**
 * Inicio: lo que hay que hacer, lo que ha pasado y, al final, el plan. Los pasos
 * de activación viven en Configuración (decisión del propietario, 2026-09-18);
 * aquí aparecen como UNA tarea más, con su botón, para que nadie nuevo se quede
 * sin saber por dónde empezar (OD-0015 D-02, D-06).
 *
 * En Business, las oportunidades van justo después de las tareas, y el plan no
 * aparece (ver la cabecera).
 */
export function PantallaInicio({
  esBusiness,
  usoFacturas,
  avisos,
  onIrAviso,
  onIrAConfiguracion,
  onIrAPresupuestos,
  onNuevoPresupuesto,
  onAbrirPresupuesto,
  onIrAOportunidades,
  onNuevaOportunidad,
  onAbrirOportunidad,
  onIrAInformes,
}: {
  /** `null` mientras no se sabe el plan: entonces se comporta como hasta ahora. */
  esBusiness: boolean | null;
  usoFacturas: UsoFacturas | null;
  avisos: readonly AvisoPendiente[] | null;
  onIrAviso: (destino: DestinoAviso) => void;
  onIrAConfiguracion: () => void;
  onIrAPresupuestos: () => void;
  onNuevoPresupuesto: () => void;
  onAbrirPresupuesto: (id: string) => void;
  onIrAOportunidades: () => void;
  onNuevaOportunidad: () => void;
  onAbrirOportunidad: (id: string) => void;
  onIrAInformes: () => void;
}) {
  // Una sola lista, con lo del alta primero. Los pasos del alta ya vienen
  // dentro de los avisos, sacados de LA lista de pasos (`ui/pasos-del-alta`),
  // así que aquí no se añade nada: sumar otra vez contaría dos veces.
  // `null` -mientras no se sabe- sigue siendo `null`: la tarjeta no se pinta
  // en vez de afirmar que no falta nada.
  const tareas = avisosDeLaTarjeta(avisos);
  return (
    <>
      <TareasPendientes tareas={tareas} onIr={onIrAviso} onIrAConfiguracion={onIrAConfiguracion} />
      {esBusiness === true ? (
        <OportunidadesAbiertas onAbrir={onAbrirOportunidad} onNueva={onNuevaOportunidad} onVerTodas={onIrAOportunidades} />
      ) : null}
      {esBusiness === true ? <ImpuestosDelTrimestre onVerInforme={onIrAInformes} /> : null}
      <ResumenNegocio
        mostrarLimites={esBusiness !== true}
        usoFacturas={usoFacturas}
        onIrAPresupuestos={onIrAPresupuestos}
        onNuevoPresupuesto={onNuevoPresupuesto}
        onAbrirPresupuesto={onAbrirPresupuesto}
      />
    </>
  );
}

/**
 * Configuración › Primeros pasos: TODOS los pasos del alta, de la misma lista
 * que el asistente y que «Tareas pendientes» (auditoría del 2026-10-29,
 * hallazgo 4). Hasta entonces esta pantalla tenía su propia cuenta, sin la
 * autorización ni el segundo factor, y decía «3 de 6» con siete filas.
 */
export function PantallaConfiguracion({
  alta,
  onIrAEmisor,
  onAbrirAsistente,
  onIrASeguridad,
  onIrAClientes,
  onIrAFacturas,
}: {
  alta: DatosDelAlta;
  onIrAEmisor: () => void;
  /** La autorización no es una pantalla: es el paso 2 del asistente (`OD-0039` E-11). */
  onAbrirAsistente: () => void;
  onIrASeguridad: () => void;
  onIrAClientes: () => void;
  onIrAFacturas: () => void;
}) {
  const pasos = pasosDelAlta(alta);
  const { hechos, total } = progresoDelAlta(pasos);

  const textoEstado = (paso: PasoAlta): string => {
    if (paso.estado === "hecho") return t.inicio.hecho;
    if (paso.estado === "automatico") return t.inicio.automatica;
    if (paso.estado === "fuera") return t.inicio.conSii;
    if (paso.estado === "desconocido") return t.inicio.sinSaber;
    if (paso.id === "fiscal" && paso.estado === "espera") return alta.sii === null ? t.inicio.faltaSii : t.inicio.guardadoSinConfirmar;
    if (paso.id === "autorizacion" && paso.estado === "espera") return t.inicio.enComprobacion;
    return t.inicio.pendiente;
  };

  const accion: Partial<Record<IdPasoAlta, { readonly texto: string; readonly ir: () => void }>> = {
    fiscal: { texto: t.inicio.irAlPaso, ir: onIrAEmisor },
    autorizacion: { texto: t.inicio.irAlPaso, ir: onAbrirAsistente },
    mfa: { texto: t.inicio.irAlPaso, ir: onIrASeguridad },
    cliente: { texto: t.inicio.irAlPaso, ir: onIrAClientes },
    factura: { texto: t.inicio.irAFacturas, ir: onIrAFacturas },
  };

  return (
    <>
      <Tarjeta titulo={t.inicio.titulo}>
        <p>{t.inicio.detalle}</p>
        <div className="progreso">
          <progress value={hechos} max={total} aria-labelledby="progreso-texto" />
          <p id="progreso-texto" className="ayuda-neutra">
            {t.inicio.progreso(hechos, total)}
          </p>
        </div>
        <ol className="pasos">
          {pasos.map((paso) => {
            const boton = paso.estado === "pendiente" || paso.estado === "espera" ? accion[paso.id] : undefined;
            return (
              <li key={paso.id} className={`paso paso-${paso.estado}`}>
                <span className="paso-marca" aria-hidden="true" />
                <span className="paso-texto">
                  <span>{t.inicio.pasos[paso.id]}</span>
                  <span className="paso-estado">{textoEstado(paso)}</span>
                </span>
                {boton !== undefined ? (
                  <Boton variante="secundario" onClick={boton.ir}>
                    {boton.texto}
                  </Boton>
                ) : null}
              </li>
            );
          })}
        </ol>
      </Tarjeta>
      <Aviso tono="info" titulo={t.inicio.siguienteTitulo}>
        {t.inicio.siguienteDetalle}
      </Aviso>
    </>
  );
}
